import {useContext, useEffect, useRef, useState} from "react";
import ReactPlayer from "react-player";
import styled from "styled-components";
import {AppMainStateContext} from "../../contexts/AppMainStateContext";
import {useAppDispatch, useAppSelector} from "../../stores/store";
import {changeVideoAsync, playerActions} from "./playerSlice";
import {usePlayerPage} from "../../hooks/usePlayerPage.tsx";
import Hls from 'hls.js'
import {useHlsDebug} from "./debug/useHlsDebug.tsx";

const ReactPlayerWrapper = styled.div`
    @media screen and (min-width: 320px) and (max-width: 767px) and (orientation: portrait) {
        html {
            transform: rotate(-90deg);
            transform-origin: left top;
            width: 100vh;
            height: 100lvh;
            overflow-x: hidden;
            position: absolute;
            top: 100%;
            left: 0;
        }
    }
`;

export const Player = () => {
    const [timeoutId, setTimeoutId] = useState(0);
    const [error, setError] = useState(false);
    const [retryCount, setRetryCount] = useState(0);
    const maxRetries = 3;
    const retryInterval = 3000; // 3 seconds
    const hlsRef = useRef<Hls | null>(null);
    const dispatch = useAppDispatch();
    const {appStates} = useContext(AppMainStateContext);
    const {setHlsQualityLevels} = usePlayerPage();
    const playerState = useAppSelector((store) => store.player);
    // const actualPlayingChannelId = useAppSelector((store) => store.playerUI).actualChannelIndex;
    const playing = useAppSelector((store) => store.player.playing);
    const reactPlayerVideoRef = useRef<ReactPlayer>(null);
    const {isIOS} = useContext(AppMainStateContext);

    const getAndSetQualityLevels = () => {
        const hlsSettings = reactPlayerVideoRef.current?.getInternalPlayer("hls");
        if (hlsSettings) {
            const hlsQualityLevels = reactPlayerVideoRef.current?.getInternalPlayer("hls").levels;
            setHlsQualityLevels(hlsQualityLevels);
            if (appStates.qualityLevelPreference === "AUTO")
                dispatch(playerActions.setQualityLevel(-1))
            if (appStates.qualityLevelPreference === "LOW")
                dispatch(playerActions.setQualityLevel(0))
            if (appStates.qualityLevelPreference === "BEST")
                setTimeout(() => {
                    dispatch(playerActions.setQualityLevel(hlsQualityLevels.length - 1))
                }, 200)
        }
    }

    const isMuted = playerState.muted || playerState.initialRun;

    const changeFocusOnOtherElem = () => {
        const markerBtn = document.getElementById("playerUITopBarMarkerBtn");
        if (markerBtn)
            markerBtn.focus();
    }

    useEffect(() => {
        if (!hlsRef.current) {
            hlsRef.current = new Hls();
        }
        return () => {
            hlsRef.current?.destroy();
            hlsRef.current = null;
        };
    }, []);

    useEffect(() => {
        const hlsSettings = reactPlayerVideoRef.current?.getInternalPlayer("hls");
        if (hlsSettings) {
            setTimeout(() => {
                hlsSettings.nextLevel = playerState.qualityLevel;
            }, 200);
        }
    }, [playerState.qualityLevel]);

    useEffect(() => {
        if (error && retryCount < maxRetries) {
            const timer = setTimeout(() => {
                setError(false);
                setRetryCount(prevCount => prevCount + 1);
            }, retryInterval);
            return () => clearTimeout(timer);
        }
    }, [error, retryCount]);

    useEffect(() => {
        setError(false);
        setRetryCount(0);
    }, [playerState.url])

    useHlsDebug(reactPlayerVideoRef);

    return (
        <ReactPlayerWrapper
            className={"relative sm:max-h-screen sm:min-h-full sm:max-w-full aspect-video my-auto mx-auto"}>
            {!error &&
                <ReactPlayer
                    ref={reactPlayerVideoRef}
                    playing={playing}
                    url={playerState.url}
                    className={"react-player"}
                    config={{
                        file: {
                            forceHLS: !isIOS,
                            forceVideo: true,
                            attributes: {
                                controls: isIOS,
                                disablePictureInPicture: false,
                            },
                            hlsOptions: {
                                startLevel: -1,
                                debug: false,
                                manifestLoadingMaxRetry: 1,
                                levelLoadingMaxRetry: 1,
                                fragLoadingMaxRetry: 1,
                                keyLoadingMaxRetry: 0,
                            },
                        },
                    }}
                    pip={true}
                    muted={isMuted}
                    onReady={() => {
                        dispatch(playerActions.setPlaying(true))
                    }}
                    onPlay={() => {
                        dispatch(playerActions.setIsError(false));
                        if (retryCount !== 0)
                            setRetryCount(0);
                        changeFocusOnOtherElem();
                    }}
                    onStart={() => {
                        getAndSetQualityLevels();
                        dispatch(playerActions.setIsError(false));
                    }}
                    onPause={() => {
                        dispatch(playerActions.setPlaying(false))
                    }}
                    onBuffer={() => dispatch(playerActions.setIsBuffer(true))}
                    onBufferEnd={() => {
                        dispatch(playerActions.setIsError(false));
                        dispatch(playerActions.setIsBuffer(false));
                        dispatch(playerActions.setPlaying(true));
                    }}
                    onDuration={() => {
                        dispatch(playerActions.setIsError(false));
                    }}
                    onProgress={() => {
                        dispatch(playerActions.setIsError(false));
                        if (retryCount !== 0)
                            setRetryCount(0);
                        if (timeoutId !== 0) {
                            clearTimeout(timeoutId);
                            setTimeoutId(0);
                        }
                    }}
                    onError={(_, data) => {
                        // console.log(error);
                        // console.log(data.response.code === 403);
                        if (data?.response?.code === 403 && playerState.actualPlayingChannel != null) {
                            dispatch(changeVideoAsync({chId: playerState.actualPlayingChannel.ch_id}));
                        }
                        // console.log(hlsInstance);
                        // console.log(hlsGlobal);
                        if (retryCount === 0) {
                            const timeoutIdTemp = setTimeout(() => {
                                setError(true);
                            }, 3000);
                            setTimeoutId(timeoutIdTemp);
                        } else {
                            setError(true);
                        }
                        if (retryCount >= maxRetries) {
                            dispatch(playerActions.setErrorStatus("Coś poszło nie tak..."));
                            dispatch(playerActions.setIsError(true));
                        }
                    }}
                    volume={playerState.volume}
                    height="100%"
                    width="100%"
                />
            }
        </ReactPlayerWrapper>
    )
}
